import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer, Form, Input, InputNumber, Select, Radio, Checkbox, Switch, Button,
    Row, Col, Space, Divider, Alert, message, Modal
} from 'antd';
import { CloseOutlined, CheckOutlined, SettingOutlined } from '@ant-design/icons';
import {
    MOCK_APPS, MOCK_DURATION_MAP, DURATION_UNIT_MAP, MOCK_TEMPLATES,
    MOCK_PKG_TAGS, MOCK_DESC_TAGS, OPERATION_POSITION_OPTIONS
} from '../mockData';
import { fenToYuan, yuanToFen } from '../../../../utils/regionPricing';
import TemplateDetailCards from './TemplateDetailCards';
import TagDialog from './TagDialog';
import MultilingualDialog from './MultilingualDialog';
import RegionPricingFields from './RegionPricingFields';

// TODO: 联调时替换为接口数据
const MOCK_PRODUCTS = [
    { value: 'P10001', label: 'P10001 · 智能门铃' },
    { value: 'P10002', label: 'P10002 · 户外摄像头' },
    { value: 'P10003', label: 'P10003 · 室内云台机' },
    { value: 'P10004', label: 'P10004 · 宠物喂食器' },
];

// 区域定价：仅覆盖售价，不含划线价；国内固定 CNY 不参与；未配置则返回 null
const buildRegionPricing = (prefix, values) => {
    const countries = (values[`${prefix}CountryOverrides`] || []).filter(r => r && r.country);
    const defaultUsd = yuanToFen(values[`${prefix}DefaultUsd`]);
    if ((defaultUsd === undefined || defaultUsd === null) && countries.length === 0) return null;
    return {
        defaultUsd: defaultUsd ?? null,
        countryOverrides: countries.map(r => ({ country: r.country, usdPrice: yuanToFen(r.usdPrice) ?? null })),
    };
};

// 手动修改区域定价字段后，对应模板选择回退为「自定义」
const REGION_TEMPLATE_FIELD_MAP = {
    noSubRegionDefaultUsd: 'noSubRegionTemplateId',
    noSubRegionCountryOverrides: 'noSubRegionTemplateId',
    subRegionDefaultUsd: 'subRegionTemplateId',
    subRegionCountryOverrides: 'subRegionTemplateId',
};

const PackageDrawer = ({ open, editData, onClose, onSave, regionTemplates = [], onRegionTemplatesChange, onManageTemplates }) => {
    const [form] = Form.useForm();
    const [pkgTagList, setPkgTagList] = useState(MOCK_PKG_TAGS);
    const [descTagList, setDescTagList] = useState(MOCK_DESC_TAGS);
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [descTagDialogOpen, setDescTagDialogOpen] = useState(false);
    const [i18n, setI18n] = useState({ open: false, title: '', originValue: '' });
    const [iapKeys, setIapKeys] = useState({ sub: [], nonSub: [] });
    const [activeIapTab, setActiveIapTab] = useState('sub');
    const [tplModal, setTplModal] = useState({ open: false, name: '', prefix: '' });

    // ── 联动监听 ──
    const applicationDirection = Form.useWatch('applicationDirection', form) || 1;
    const selectedDurationKey = Form.useWatch('selectedDurationKey', form);
    const templateIds = Form.useWatch('templateId', form) || [];
    const productLimitType = Form.useWatch('productLimitType', form) || 0;
    // 以下三个开关为手动受控 Checkbox，未注册为表单字段；
    // useWatch 默认只读取「已注册字段」，需 preserve: true 才能从完整 store 取值，否则联动恒为 undefined。
    const isNoSubscription = Form.useWatch('isNoSubscription', { form, preserve: true }) || 0;
    const isSubscription = Form.useWatch('isSubscription', { form, preserve: true }) || 0;
    const isFree = Form.useWatch('isFree', { form, preserve: true }) || 0;
    const freeTrial = Form.useWatch('freeTrial', form);
    const trialType = Form.useWatch('trialType', form) || 'free';
    const releaseArea = Form.useWatch('releaseArea', form) || [];
    const hasOverseas = releaseArea.includes('2');

    const isEdit = !!editData;
    const durationOptions = useMemo(
        () => (MOCK_DURATION_MAP[applicationDirection] || []).map(d => ({
            label: `${d.serviceDuration}${DURATION_UNIT_MAP[d.serviceDurationUnit] || ''}`,
            value: `${d.serviceDuration}-${d.serviceDurationUnit}`,
        })),
        [applicationDirection]
    );
    const availableTemplates = useMemo(
        () => MOCK_TEMPLATES.filter(t => t.direction === applicationDirection && (!selectedDurationKey || t.durations.includes(selectedDurationKey))),
        [applicationDirection, selectedDurationKey]
    );
    const activePricingTabs = useMemo(() => {
        const tabs = [];
        if (isSubscription === 1) tabs.push({ key: 'sub', label: '订阅制' });
        if (isNoSubscription === 1) tabs.push({ key: 'nonSub', label: '非订阅' });
        return tabs;
    }, [isSubscription, isNoSubscription]);

    // 定价类型变化时同步内购Key Tab
    useEffect(() => {
        if (activePricingTabs.length === 0) setActiveIapTab('');
        else if (!activePricingTabs.find(t => t.key === activeIapTab)) setActiveIapTab(activePricingTabs[0].key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubscription, isNoSubscription]);

    // 打开 / 回显
    useEffect(() => {
        if (!open) return;
        const emptyIap = {
            sub: MOCK_APPS.map(a => ({ ...a, googleKey: '', appleKey: '' })),
            nonSub: MOCK_APPS.map(a => ({ ...a, googleKey: '', appleKey: '' })),
        };
        if (editData) {
            form.setFieldsValue({
                name: editData.name,
                applicationDirection: editData.applicationDirection ?? 1,
                selectedDurationKey: undefined,
                templateId: [],
                deviceNum: 1,
                deviceBuyCount: 0,
                userBuyCount: 0,
                productLimitType: 0,
                productLimit: [],
                isNoSubscription: editData.isNoSubscription ?? 0,
                isSubscription: editData.isSubscription ?? 0,
                isFree: editData.isFree ?? 0,
                noSubPriceCNY: fenToYuan(editData.noSubscriptionPriceCny),
                noSubRegionDefaultUsd: fenToYuan(editData.noSubscriptionRegionPricing?.defaultUsd),
                noSubRegionCountryOverrides: (editData.noSubscriptionRegionPricing?.countryOverrides || []).map(c => ({
                    country: c.country, usdPrice: fenToYuan(c.usdPrice),
                })),
                noSubRegionTemplateId: 'CUSTOM',
                subDesc: '',
                salePriceCNY: fenToYuan(editData.salePriceCny),
                subRegionDefaultUsd: fenToYuan(editData.subscriptionRegionPricing?.defaultUsd),
                subRegionCountryOverrides: (editData.subscriptionRegionPricing?.countryOverrides || []).map(c => ({
                    country: c.country, usdPrice: fenToYuan(c.usdPrice),
                })),
                subRegionTemplateId: 'CUSTOM',
                freeTrial: 2,
                trialType: 'free',
                trialPriceCNY: undefined,
                trialPriceUSD: undefined,
                trialDay: 7,
                trialCount: 1,
                trialCondition: 0,
                landingPageCN: '',
                landingPageEN: '',
                packageLabel: editData.packageLabel || [],
                descLabel: [],
                releaseArea: editData.releaseArea || [],
                releaseApp: (editData.releaseAppList || []).map(a => MOCK_APPS.find(m => m.appName === a.appName)?.appId).filter(Boolean),
                releaseOpsPosition: editData.releaseOpsPosition || [],
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                applicationDirection: 1,
                deviceNum: 1,
                deviceBuyCount: 0,
                userBuyCount: 0,
                productLimitType: 0,
                productLimit: [],
                isNoSubscription: 0,
                isSubscription: 0,
                isFree: 0,
                templateId: [],
                packageLabel: [],
                descLabel: [],
                releaseArea: [],
                releaseApp: [],
                releaseOpsPosition: [],
                noSubRegionDefaultUsd: undefined,
                noSubRegionCountryOverrides: [],
                noSubRegionTemplateId: 'CUSTOM',
                subRegionDefaultUsd: undefined,
                subRegionCountryOverrides: [],
                subRegionTemplateId: 'CUSTOM',
                freeTrial: 2,
                trialType: 'free',
                trialCount: 1,
                trialCondition: 0,
            });
        }
        setIapKeys(emptyIap);
    }, [open, editData, form]);

    // 模板被删除后，回退下拉选择为「自定义」（表单里已填的区域定价值保留）
    useEffect(() => {
        if (!open) return;
        const validIds = new Set(regionTemplates.map(t => t.id));
        const patch = {};
        ['noSubRegionTemplateId', 'subRegionTemplateId'].forEach(field => {
            const v = form.getFieldValue(field);
            if (v && v !== 'CUSTOM' && !validIds.has(v)) patch[field] = 'CUSTOM';
        });
        if (Object.keys(patch).length) form.setFieldsValue(patch);
    }, [open, regionTemplates, form]);

    const handleSubmit = async () => {
        const values = await form.validateFields();
        if (isFree !== 1 && isSubscription !== 1 && isNoSubscription !== 1) {
            message.warning('请至少选择一种订阅类型');
            return;
        }
        onSave({
            ...editData,
            packageId: editData?.packageId || `PKG${Date.now().toString().slice(-8)}`,
            name: values.name,
            packageLabel: values.packageLabel || [],
            applicationDirection: values.applicationDirection,
            releaseArea: values.releaseArea || [],
            releaseAppList: (values.releaseApp || []).map(appId => {
                const app = MOCK_APPS.find(a => a.appId === appId);
                return { appName: app?.appName || appId, appCount: 1 };
            }),
            releaseOpsPosition: values.releaseOpsPosition || [],
            isFree,
            isSubscription,
            isNoSubscription,
            platformCostCny: editData?.platformCostCny ?? null,
            platformCostUsd: editData?.platformCostUsd ?? null,
            salePriceCny: yuanToFen(values.salePriceCNY) ?? null,
            subscriptionRegionPricing: buildRegionPricing('subRegion', values),
            noSubscriptionPriceCny: yuanToFen(values.noSubPriceCNY) ?? null,
            noSubscriptionRegionPricing: buildRegionPricing('noSubRegion', values),
            createdAt: editData?.createdAt || Math.floor(Date.now() / 1000),
        });
    };

    const updateIapKey = (pt, appCode, field, value) => {
        setIapKeys(prev => ({
            ...prev,
            [pt]: prev[pt].map(item => (item.appCode === appCode ? { ...item, [field]: value } : item)),
        }));
    };

    // 另存为模板（按当前定价块取值）
    const handleSaveRegionTemplate = () => {
        const { prefix } = tplModal;
        const name = tplModal.name.trim();
        if (!name) { message.warning('请输入模板名称'); return; }
        if (regionTemplates.some(t => t.name === name)) { message.warning('模板名称已存在'); return; }
        const values = form.getFieldsValue();
        const countries = (values[`${prefix}CountryOverrides`] || []).filter(r => r && r.country);
        const defaultUsd = yuanToFen(values[`${prefix}DefaultUsd`]);
        if ((defaultUsd === undefined || defaultUsd === null) && countries.length === 0) {
            message.warning('请先配置区域定价后再另存为模板');
            return;
        }
        const tpl = {
            id: `rpt_${Date.now()}`,
            name,
            defaultUsd: defaultUsd ?? null,
            countryOverrides: countries.map(r => ({ country: r.country, usdPrice: yuanToFen(r.usdPrice) ?? null })),
        };
        onRegionTemplatesChange([...regionTemplates, tpl]);
        form.setFieldsValue({ [`${prefix}TemplateId`]: tpl.id });
        setTplModal({ open: false, name: '', prefix: '' });
        message.success(`已另存为模板「${name}」`);
    };

    return (
        <Drawer
            title={isEdit ? '编辑套餐' : '创建套餐'}
            width={800}
            open={open}
            onClose={onClose}
            destroyOnClose
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button icon={<CloseOutlined />} onClick={onClose}>取消</Button>
                        <Button type="primary" icon={<CheckOutlined />} onClick={handleSubmit}>保存</Button>
                    </Space>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={(changed) => {
                    // 手动修改区域定价字段后，对应模板选择回退为「自定义」
                    const nextTemplate = {};
                    Object.keys(changed).forEach(k => {
                        if (REGION_TEMPLATE_FIELD_MAP[k]) nextTemplate[REGION_TEMPLATE_FIELD_MAP[k]] = 'CUSTOM';
                    });
                    if (Object.keys(nextTemplate).length) form.setFieldsValue(nextTemplate);
                }}
            >
                {/* 套餐名称 */}
                <Form.Item
                    label={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>套餐名称</span>
                            <Button type="link" size="small" icon={null} onClick={() => setI18n({ open: true, title: '套餐名称—多语言翻译', originValue: form.getFieldValue('name') })}>
                                配置多语言
                            </Button>
                        </div>
                    }
                    name="name"
                    rules={[{ required: true, message: '请输入套餐名称' }]}
                >
                    <Input placeholder="请输入套餐名称" maxLength={30} showCount />
                </Form.Item>

                {/* 选择套餐服务 */}
                <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 20 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 16 }}>选择套餐服务</div>

                    <Form.Item label="应用方向" name="applicationDirection" rules={[{ required: true, message: '请选择应用方向' }]}>
                        <Radio.Group
                            options={[{ label: '设备套餐', value: 1 }, { label: '用户套餐', value: 2 }]}
                            onChange={() => form.setFieldsValue({ selectedDurationKey: undefined, templateId: [] })}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="生效时长" name="selectedDurationKey" rules={[{ required: true, message: '请选择生效时长' }]}>
                                <Select
                                    placeholder="请选择"
                                    allowClear
                                    options={durationOptions}
                                    onChange={() => form.setFieldsValue({ templateId: [] })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="套餐模板" name="templateId" rules={[{ required: true, message: '请选择套餐模板' }]}>
                                <Select
                                    mode="multiple"
                                    placeholder="选择模板"
                                    allowClear
                                    disabled={!selectedDurationKey}
                                    options={availableTemplates.map(t => ({ label: t.name, value: t.id }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <TemplateDetailCards templateIds={templateIds} templateList={availableTemplates} />
                </div>

                {/* 可绑定设备数（仅设备套餐） */}
                {applicationDirection === 1 && (
                    <Form.Item label="可绑定设备数" name="deviceNum" rules={[{ required: true, message: '请输入可绑定设备数' }]}>
                        <InputNumber min={0} max={9999} style={{ width: '100%' }} placeholder="请输入可绑定设备数" />
                    </Form.Item>
                )}

                {/* 购买限制设置 */}
                <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 20 }}>
                    <Form.Item
                        label={<span style={{ fontWeight: 600 }}>限制设备购买次数</span>}
                        name="deviceBuyCount"
                        rules={[{ required: true, message: '请输入购买次数上限' }]}
                    >
                        <InputNumber min={0} max={9999} style={{ width: '100%' }} placeholder="0 表示不限制" addonAfter="次" />
                    </Form.Item>
                    <Divider style={{ margin: '4px 0 16px' }} />
                    <Form.Item
                        label={<span style={{ fontWeight: 600 }}>限制用户购买次数</span>}
                        name="userBuyCount"
                        rules={[{ required: true, message: '请输入购买次数上限' }]}
                    >
                        <InputNumber min={0} max={9999} style={{ width: '100%' }} placeholder="0 表示不限制" addonAfter="次" />
                    </Form.Item>
                    <Divider style={{ margin: '4px 0 16px' }} />
                    <Form.Item
                        label={<span style={{ fontWeight: 600 }}>限制产品购买</span>}
                        name="productLimitType"
                    >
                        <Radio.Group options={[{ label: '不限制', value: 0 }, { label: '黑名单', value: 2 }, { label: '白名单', value: 1 }]} />
                    </Form.Item>
                    {productLimitType > 0 && (
                        <Form.Item
                            label={productLimitType === 1 ? '白名单产品' : '黑名单产品'}
                            name="productLimit"
                            rules={[{ required: true, type: 'array', min: 1, message: '请选择产品' }]}
                        >
                            <Select mode="multiple" placeholder="搜索并选择产品ID" options={MOCK_PRODUCTS} showSearch optionFilterProp="label" />
                        </Form.Item>
                    )}
                </div>

                {/* 套餐定价 */}
                <Form.Item label="套餐定价">
                    <Space size={24}>
                        <Checkbox
                            checked={isNoSubscription === 1}
                            onChange={e => form.setFieldsValue({ isNoSubscription: e.target.checked ? 1 : 0, isFree: 0 })}
                        >非订阅</Checkbox>
                        <Checkbox
                            checked={isSubscription === 1}
                            onChange={e => form.setFieldsValue({ isSubscription: e.target.checked ? 1 : 0, isFree: 0 })}
                        >订阅制</Checkbox>
                        <Checkbox
                            checked={isFree === 1}
                            onChange={e => form.setFieldsValue({ isFree: e.target.checked ? 1 : 0, isSubscription: 0, isNoSubscription: 0 })}
                        ><span style={{ color: '#e6a23c' }}>免费套餐</span></Checkbox>
                    </Space>
                </Form.Item>

                {isFree === 1 && (
                    <Alert type="warning" showIcon message="免费套餐用户无需付费，请仔细核对套餐内容和服务范围。" style={{ marginBottom: 16 }} />
                )}

                {/* 非订阅定价 */}
                {isNoSubscription === 1 && isFree !== 1 && (
                    <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 20 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 16 }}>非订阅定价</div>

                        <Form.Item label="售价（¥）" name="noSubPriceCNY" rules={[{ required: true, message: '请输入售价' }]}>
                            <InputNumber min={0} precision={2} controls={false} prefix="¥" placeholder="0.00" style={{ width: '100%' }} />
                        </Form.Item>

                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 12px' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#606266' }}>非订阅售价区域定价（海外）</span>
                            <span style={{ fontSize: 12, color: '#909399' }}>仅针对售价，不含划线价</span>
                        </div>
                        <RegionPricingFields
                            form={form}
                            prefix="noSubRegion"
                            templates={regionTemplates}
                            hasOverseas={hasOverseas}
                            onSaveTemplate={() => setTplModal({ open: true, name: '', prefix: 'noSubRegion' })}
                            onManageTemplates={onManageTemplates}
                        />
                    </div>
                )}

                {/* 订阅制定价 */}
                {isSubscription === 1 && isFree !== 1 && (
                    <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 20 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 16 }}>订阅制定价</div>

                        <Form.Item
                            label={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>订阅文案</span>
                                    <Button type="link" size="small" onClick={() => setI18n({ open: true, title: '订阅文案—多语言翻译', originValue: form.getFieldValue('subDesc') })}>配置多语言</Button>
                                </div>
                            }
                            name="subDesc"
                            rules={[{ required: true, message: '请输入订阅文案' }]}
                        >
                            <Input placeholder="如：每月自动续费，可随时取消" />
                        </Form.Item>

                        <Form.Item label="订阅售价（¥）" name="salePriceCNY" rules={[{ required: true, message: '请输入订阅售价' }]}>
                            <InputNumber min={0} precision={2} controls={false} prefix="¥" placeholder="0.00" style={{ width: '100%' }} />
                        </Form.Item>

                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 12px' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#606266' }}>订阅售价区域定价（海外）</span>
                            <span style={{ fontSize: 12, color: '#909399' }}>仅针对售价，不含划线价</span>
                        </div>
                        <RegionPricingFields
                            form={form}
                            prefix="subRegion"
                            templates={regionTemplates}
                            hasOverseas={hasOverseas}
                            onSaveTemplate={() => setTplModal({ open: true, name: '', prefix: 'subRegion' })}
                            onManageTemplates={onManageTemplates}
                        />

                        <div style={{ height: 8 }} />

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                            <span style={{ fontWeight: 500 }}>允许试用</span>
                            <Form.Item name="freeTrial" valuePropName="checked" noStyle getValueProps={v => ({ checked: v === 1 })} getValueFromEvent={e => (e ? 1 : 2)}>
                                <Switch />
                            </Form.Item>
                        </div>

                        {freeTrial === 1 && (
                            <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                                <Form.Item label="试用类型" name="trialType">
                                    <Radio.Group options={[{ label: '免费试用', value: 'free' }, { label: '付费试用', value: 'paid' }]} />
                                </Form.Item>
                                {trialType === 'paid' && (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item label="试用售价（¥）" name="trialPriceCNY" rules={[{ required: true, message: '试用售价必须大于0' }]}>
                                                <InputNumber min={0} precision={2} controls={false} prefix="¥" placeholder="0.00" style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="试用售价（$）" name="trialPriceUSD" rules={[{ required: true, message: '试用售价必须大于0' }]}>
                                                <InputNumber min={0} precision={2} controls={false} prefix="$" placeholder="0.00" style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}
                                <Row gutter={16}>
                                    {trialType === 'free' ? (
                                        <Col span={12}>
                                            <Form.Item label="试用周期（天）" name="trialDay" rules={[{ required: true, message: '请输入试用周期' }]}>
                                                <InputNumber min={1} max={365} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    ) : (
                                        <Col span={12}>
                                            <Form.Item label="试用期数"><InputNumber value={1} min={1} max={1} disabled style={{ width: '100%' }} /></Form.Item>
                                        </Col>
                                    )}
                                    <Col span={12}>
                                        <Form.Item label="过滤条件" name="trialCondition">
                                            <Select
                                                options={[
                                                    { label: '从未试用过该服务类型', value: 1 },
                                                    { label: '从未试用过此套餐', value: 2 },
                                                    { label: '不限制', value: 0 },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                )}

                {/* 套餐介绍 */}
                <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 12 }}>套餐介绍</div>
                <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 20 }}>
                    <Form.Item label="国内落地页URL" name="landingPageCN">
                        <Input placeholder="请输入国内落地页URL，例如：https://www.example.com/cn/package/xxx" />
                    </Form.Item>
                    <Form.Item label="海外落地页URL" name="landingPageEN">
                        <Input placeholder="请输入海外落地页URL，例如：https://www.example.com/en/package/xxx" />
                    </Form.Item>

                    {isFree !== 1 && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
                                <span style={{ fontWeight: 600 }}>内购KEY配置</span>
                                <span style={{ fontSize: 12, color: '#909399', marginLeft: 8 }}>选填，针对每个APP配置</span>
                            </div>
                            {activePricingTabs.length === 0 ? (
                                <span style={{ fontSize: 12, color: '#909399' }}>请先在『套餐定价』中勾选定价类型</span>
                            ) : (
                                <>
                                    <Radio.Group
                                        value={activeIapTab}
                                        onChange={e => setActiveIapTab(e.target.value)}
                                        optionType="button"
                                        style={{ marginBottom: 12 }}
                                        options={activePricingTabs}
                                    />
                                    {(iapKeys[activeIapTab] || []).map(item => (
                                        <div key={item.appCode} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                                            <div style={{ width: 140, flexShrink: 0, fontWeight: 600 }}>{item.appName}</div>
                                            <Input placeholder="Google 内购 Key" value={item.googleKey} onChange={e => updateIapKey(activeIapTab, item.appCode, 'googleKey', e.target.value)} />
                                            <Input placeholder="Apple 内购 Key" value={item.appleKey} onChange={e => updateIapKey(activeIapTab, item.appCode, 'appleKey', e.target.value)} />
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* 套餐标签 */}
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>套餐标签</span>
                    <Button type="link" size="small" icon={<SettingOutlined />} style={{ marginLeft: 8 }} onClick={() => setTagDialogOpen(true)}>管理</Button>
                </div>
                <Form.Item name="packageLabel">
                    <Select mode="multiple" placeholder="请选择套餐标签" options={pkgTagList.map(t => ({ label: t.name, value: t.name }))} />
                </Form.Item>

                {/* 套餐描述标签 */}
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>套餐描述标签</span>
                    <Button type="link" size="small" icon={<SettingOutlined />} style={{ marginLeft: 8 }} onClick={() => setDescTagDialogOpen(true)}>管理</Button>
                </div>
                <Form.Item name="descLabel">
                    <Select mode="multiple" placeholder="请选择套餐描述标签" options={descTagList.map(t => ({ label: t.name, value: t.name }))} />
                </Form.Item>

                {/* 其他标签配置 */}
                <div style={{ fontSize: 15, fontWeight: 600, color: '#303133', marginBottom: 12 }}>其他标签配置</div>
                <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20 }}>
                    <Form.Item label="投放区域" name="releaseArea">
                        <Select mode="multiple" placeholder="请选择投放区域" options={[{ label: '国内', value: '1' }, { label: '海外', value: '2' }]} />
                    </Form.Item>
                    <Form.Item label="投放APP" name="releaseApp">
                        <Select mode="multiple" placeholder="请选择投放APP" optionFilterProp="label" showSearch options={MOCK_APPS.map(a => ({ label: a.appName, value: a.appId }))} />
                    </Form.Item>
                    <Form.Item label="投放运营位" name="releaseOpsPosition">
                        <Select mode="multiple" placeholder="请选择投放运营位" options={OPERATION_POSITION_OPTIONS.map(v => ({ label: v, value: v }))} />
                    </Form.Item>
                </div>

            </Form>

            <TagDialog open={tagDialogOpen} type={2} list={pkgTagList} onChange={setPkgTagList} onCancel={() => setTagDialogOpen(false)} />
            <TagDialog open={descTagDialogOpen} type={1} list={descTagList} onChange={setDescTagList} onCancel={() => setDescTagDialogOpen(false)} />
            <MultilingualDialog open={i18n.open} title={i18n.title} originValue={i18n.originValue} onCancel={() => setI18n({ open: false, title: '', originValue: '' })} />

            <Modal
                title={`另存为区域定价模板（${tplModal.prefix === 'subRegion' ? '订阅售价' : '非订阅售价'}）`}
                open={tplModal.open}
                okText="保存"
                cancelText="取消"
                onOk={handleSaveRegionTemplate}
                onCancel={() => setTplModal({ open: false, name: '', prefix: '' })}
                destroyOnClose
            >
                <div style={{ marginBottom: 8 }}>模板名称</div>
                <Input
                    value={tplModal.name}
                    onChange={(e) => setTplModal(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="请输入模板名称"
                    maxLength={30}
                    showCount
                />
                <div style={{ fontSize: 12, color: '#909399', marginTop: 8 }}>保存后可在对应定价块的「区域定价模板」下拉中复用</div>
            </Modal>
        </Drawer>
    );
};

export default PackageDrawer;
